/**
 * Prestige — l'écran qui rouvre le jeu quand il semblait fini (§6, D§4).
 *
 * ══ Pourquoi cet écran existe, et pourquoi il est dans « Progression » ═══════
 *
 * L'audit du 17 août a mesuré le défaut le plus grave du jeu : **les douze extensions de
 * terrain sont achetées en 4,3 h**, et rien n'ouvrait derrière. Le principe premier du §1
 * l'interdit — « il ne doit jamais exister d'état j'ai fini ». Le prestige est la
 * réponse, et sa place est l'onglet où le joueur regarde ce qu'il a accompli.
 *
 * ══ Trois règles d'écran, non négociables ════════════════════════════════════
 *
 * **Le gain s'annonce AVANT d'être pris.** Le joueur va perdre sa ville : il doit voir
 * exactement ce qu'il reçoit en échange, en chiffres, sans avoir à cliquer pour le
 * découvrir. Un prestige qu'on regrette est un joueur qu'on perd.
 *
 * **Ce qui survit est écrit noir sur blanc.** « Tu perds ta ville » est terrifiant tant
 * qu'on ne sait pas que les gemmes, les jalons et le multiplicateur restent. L'inquiétude
 * ne vient pas de la perte, elle vient de l'incertitude.
 *
 * **Indisponible ne veut pas dire muet.** Tant que le seuil n'est pas atteint, le panneau
 * montre la distance qui reste — c'est un objectif, donc exactement ce que le §1 réclame
 * à chaque instant de la partie.
 */

import { PRESTIGE } from '@/config/balance';
import { nextEraUnlock } from '@/core/economy';
import { prestigeProgress } from '@/core/gameState';
import type { CitySnapshot } from '@/core/gameState';
import { prestigePreview } from '@/core/prestige';
import { duration, n, t } from '@/i18n';
import type { TranslationKey } from '@/i18n';
import { createButton, createPanel } from './components';
import type { ButtonHandle } from './components';
import { icon } from './icons';

export interface PrestigePanelCallbacks {
  /** Le joueur a confirmé : on ouvre une nouvelle ère. */
  readonly onPrestige: () => void;
}

export interface PrestigePanelHandle {
  readonly el: HTMLElement;
  update(snapshot: CitySnapshot): void;
  destroy(): void;
}

export function createPrestigePanel(callbacks: PrestigePanelCallbacks): PrestigePanelHandle {
  const title = document.createElement('h3');
  title.className = 'prestige__title';
  title.append(icon('star'), t('prestige.title'));

  const era = document.createElement('p');
  era.className = 'prestige__era';

  const pitch = document.createElement('p');
  pitch.className = 'prestige__pitch';
  pitch.textContent = t('prestige.pitch');

  /* ── L'offre chiffrée ────────────────────────────────────────────────── */
  const gain = document.createElement('div');
  gain.className = 'prestige__gain';

  const bar = document.createElement('div');
  bar.className = 'prestige__bar';
  const fill = document.createElement('i');
  bar.append(fill);

  const progress = document.createElement('p');
  progress.className = 'prestige__progress';

  /* ── Ce qui survit — la moitié qui rassure ───────────────────────────── */
  const keeps = document.createElement('ul');
  keeps.className = 'prestige__keeps';
  for (const key of [
    'prestige.keepsGems',
    'prestige.keepsMilestones',
    'prestige.keepsBonus',
  ] as const) {
    const item = document.createElement('li');
    item.textContent = t(key);
    keeps.append(item);
  }

  /*
    ⚠️ **Ce que la prochaine ère DÉBLOQUE, annoncé avant le reset.**

    « Recommencer, c'est perdre sa ville » est une phrase difficile à entendre tant qu'on
    ne sait pas ce qu'on gagne en échange. Un multiplicateur est un nombre qui monte : il
    ne se voit pas à l'écran, et il ne donne pas envie. Un bâtiment inédit, si.

    Cette ligne est donc à sa place exacte — juste avant ce qu'on perd, jamais après.
  */
  const unlock = document.createElement('p');
  unlock.className = 'prestige__unlock';

  const loses = document.createElement('p');
  loses.className = 'prestige__loses';
  loses.textContent = t('prestige.loses');

  const action: ButtonHandle = createButton({
    label: t('prestige.action'),
    variant: 'primary',
    size: 'lg',
    icon: 'star',
    onClick: callbacks.onPrestige,
  });
  // Nommé pour que les sondes le VISENT au lieu de compter les boutons : un sélecteur
  // positionnel est une supposition sur l'ordre du DOM (règle 22).
  action.el.classList.add('prestige__action');

  /*
    ── CE QU'ON A DÉJÀ TRAVERSÉ ──────────────────────────────────────────────

    Le panneau ne parlait que de l'avenir : ce que la prochaine ère débloque, ce que le
    prochain multiplicateur vaudra. Rien ne disait au joueur ce qu'il avait ACCOMPLI —
    or le prestige demande de sacrifier sa ville, et on n'accepte de recommencer que si
    les fois précédentes ont laissé une trace.

    Trois faits, tous déjà dans la sauvegarde : combien d'ères traversées, depuis quand
    court celle-ci, et ce que la ville a produit depuis toujours. Le prestige devient
    alors une collection plutôt qu'un deuil — c'est l'argument des monuments d'héritage
    (D.2), en attendant leurs modèles.

    ⚠️ La ligne est MUETTE à la première ère : « 0 ère traversée » sonne comme un
    reproche adressé à un joueur qui n'a encore rien pu faire.
  */
  const history = document.createElement('p');
  history.className = 'prestige__history';

  const panel = createPanel({
    elevation: 'raised',
    children: [title, era, history, pitch, gain, bar, progress, keeps, unlock, loses, action.el],
  });
  panel.el.classList.add('prestige');

  return {
    el: panel.el,

    update(snapshot) {
      const preview = prestigePreview(prestigeProgress(snapshot), snapshot.prestigeMultiplier);

      // « Ère 1 » et non « prestige 0 » : un rang se compte à partir de un, et le joueur
      // ne sait pas ce qu'est un niveau de prestige avant d'en avoir fait un.
      era.textContent = t('prestige.era', {
        value: String(snapshot.prestigeLevel + 1),
        multiplier: n(snapshot.prestigeMultiplier),
      });

      history.hidden = snapshot.prestigeLevel <= 0;
      if (snapshot.prestigeLevel > 0) {
        history.textContent = t('prestige.history', {
          eras: String(snapshot.prestigeLevel),
          age: duration(Math.max(0, Date.now() - snapshot.eraStartedAt)),
          total: n(snapshot.totalProduced),
        });
      }

      /*
        Le catalogue est fini : au-delà, il n'y a plus de type à offrir. On se TAIT
        plutôt que d'annoncer un déblocage qui n'arrivera pas — promettre puis ne rien
        donner coûte plus cher que de ne rien promettre. Le multiplicateur, les jalons
        et les quêtes, eux, continuent sans fin (§1).
      */
      const nextType = nextEraUnlock(snapshot.prestigeLevel);
      unlock.hidden = nextType === undefined;
      if (nextType !== undefined) {
        unlock.textContent = t('prestige.unlocks', {
          value: t(`buildings.${nextType}` as TranslationKey),
        });
      }

      gain.replaceChildren();
      if (preview.available) {
        // L'offre, en gros : les étoiles gagnées et le multiplicateur qui en découle.
        const stars = document.createElement('b');
        stars.className = 'prestige__stars';
        stars.append(icon('star', { size: 'large' }), `+${String(preview.stars)}`);

        const next = document.createElement('span');
        next.className = 'prestige__next';
        next.textContent = t('prestige.becomes', {
          from: n(snapshot.prestigeMultiplier),
          to: n(preview.nextMultiplier),
        });

        gain.append(stars, next);
      } else {
        const soon = document.createElement('span');
        soon.className = 'prestige__soon';
        soon.textContent = t('prestige.locked', { value: n(preview.remaining) });
        gain.append(soon);
      }

      /*
        La barre mesure la PRODUCTION DE L'ÈRE vers le seuil, pas les étoiles.

        Les étoiles suivent une racine cubique : une barre qui les afficherait resterait
        collée près du maximum pendant des heures, ce qui se lit comme un défaut. La
        production, elle, avance visiblement.
      */
      const needed = PRESTIGE.scale * PRESTIGE.minStars ** 3;
      const ratio = Math.max(0, Math.min(1, preview.produced / needed));
      fill.style.width = `${(ratio * 100).toFixed(1)}%`;

      progress.textContent = t('prestige.produced', {
        value: n(preview.produced),
        target: n(needed),
      });

      action.setDisabled(!preview.available);
      action.setLabel(
        preview.available
          ? t('prestige.actionReady', { value: n(preview.gain) })
          : t('prestige.action'),
      );
    },

    destroy() {
      panel.el.remove();
    },
  };
}
